import { FlyoutContainer } from '@cmhc-schl/hearth-ui';
import { Box } from '@mui/material';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';
import ViewHistoricalRemarksData from './ViewHistoricalRemarksData';
import ViewHistoricalRemarksFlyoutHeader from './ViewHistoricalRemarksFlyoutHeader';
import { formatDateTime } from 'Feature/V2/Shared_Portfolio/utils/formatDateTime';
import { useAuthenticationContext } from 'Foundation/Authentication';
import { useDecisionOverrideRationales } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtIndividualLoanDetails/hooks/useDecisionOverrideRationales';
import { RemarkItem, mergedRemarkItem } from '../../types';
import i18n from 'i18next';
import ErrorMessage, { SpinnerElement } from '../ErrorMessage';
import {
  POOL_SUBMISSION_PACKAGE_CODES,
  OFFER_EMAIL_CODES,
  REASON_FOR_WITHDRAWAL,
  OTHER_REMARK_CODES
} from 'Feature/V2/HbtMasterPool/constants';
import { isRemarkOther } from 'Feature/V2/Shared_Portfolio/utils/remarkOther';

interface ViewHistoricalRemarksFlyoutProps {
  openFlyout: boolean;
  toggleFlyout: (event: React.KeyboardEvent | React.MouseEvent) => void;
  selectedRowsData: (number | null)[];
  remarksResponse: RemarkItem[] | undefined;
  isRemarksLoading: boolean;
  isRemarksError: boolean;
}

const ViewHistoricalRemarksFlyout = ({
  openFlyout,
  toggleFlyout,
  selectedRowsData,
  remarksResponse,
  isRemarksLoading,
  isRemarksError
}: ViewHistoricalRemarksFlyoutProps) => {
  const { t } = useTranslation('HbtMasterPool');
  const authenticationContext = useAuthenticationContext();
  const [loanInventoryError, setLoanInventoryError] = useState<string>('');

  const {
    data: rationaleResponse,
    isLoading: isRationaleLoading,
    isError: isRationaleError
  } = useDecisionOverrideRationales(authenticationContext);
  const masterPoolNumber = selectedRowsData[0] ? +selectedRowsData[0] : 0;

  const isLoading = isRationaleLoading || isRemarksLoading;
  const isError = isRationaleError || isRemarksError;

  const mergedData = useMemo(() => {
    if (!rationaleResponse || !remarksResponse) return [];

    return remarksResponse.map((remark) => {
      const match = rationaleResponse.find((rationale) => rationale.id === remark.remarkId);
      let remarkHeader = '';

      switch (true) {
        case POOL_SUBMISSION_PACKAGE_CODES.includes(remark.remarkId):
          remarkHeader = t(
            'Masterpool-Flyout-Add-Remark-Dropdown-Option-Header-Pool-Submission-Package'
          );
          break;

        case OFFER_EMAIL_CODES.includes(remark.remarkId):
          remarkHeader = t(
            'Masterpool-Flyout-Add-Remark-Dropdown-Option-Header-Letter-Of-Offer-Email'
          );
          break;

        case REASON_FOR_WITHDRAWAL.includes(remark.remarkId):
          remarkHeader = t('Pi-Master-Pool-Inventory-Table-Column-ReasonForWithdrawal');
          break;

        case isRemarkOther({ remarkID: remark.remarkId, remarkMessage: remark.remarkText }):
        case OTHER_REMARK_CODES.includes(remark.remarkId):
          remarkHeader = t('Masterpool-Flyout-Add-Remark-Dropdown-Option-Header-Other');
          break;

        default:
          remarkHeader = t('Masterpool-Flyout-Add-Remark-Dropdown-Option-Header-Other');
      }

      return {
        ...remark,
        remarkHeader,
        englishRationale: match?.englishRationale ?? null,
        frenchRationale: match?.frenchRationale ?? null
      };
    });
  }, [rationaleResponse, remarksResponse]);

  const renderHistory = (data: mergedRemarkItem[]) => {
    if (!data) return <></>;

    const rowData = data.map((d) => {
      const timestamp = formatDateTime(d.recordCreatedTimestamp ?? '');
      const dateStamp = timestamp?.date ?? '';
      const timeStamp = timestamp?.time ?? '';
      const header = d.remarkHeader;
      const editor = `${t('Editor')}: ${d.lastName}, ${d.firstName}`;
      let remark = i18n.language === 'en' ? d.englishRationale : d.frenchRationale;

      if (
        isRemarkOther({ remarkID: d.remarkId, remarkMessage: d.remarkText }) ||
        OTHER_REMARK_CODES.includes(d.remarkId)
      ) {
        remark = d.remarkText;
      }

      return (
        <ViewHistoricalRemarksData
          header={header}
          editor={editor}
          remark={remark ?? ''}
          timeStamp={timeStamp}
          dateStamp={dateStamp}
        />
      );
    });

    return [...rowData];
  };

  useEffect(() => {
    if (isError) {
      setLoanInventoryError('api_error');
    } else if (!isLoading && remarksResponse && remarksResponse.length > 0) {
      setLoanInventoryError('');
    } else if (!isLoading && remarksResponse && remarksResponse.length === 0) {
      setLoanInventoryError('no_data_error');
    }
  }, [isError, isLoading, remarksResponse]);

  return (
    <FlyoutContainer
      id={styles.HistoricalFlyout}
      isHeaderRequired={false}
      open={openFlyout}
      onClose={toggleFlyout}
      className={styles.historicalFlyoutWithOverflow}
      content={
        <Box className={styles.HistoricalDataFlyout}>
          <ViewHistoricalRemarksFlyoutHeader
            data={mergedData}
            toggleFlyout={toggleFlyout}
            masterPoolNumber={masterPoolNumber}
          />
          {isLoading && (
            <Box className={styles.spinnerElementWrapper}>
              <SpinnerElement />
            </Box>
          )}
          {!isLoading && loanInventoryError.length > 0 && (
            <Box
              sx={{
                padding: '32px;'
              }}
            >
              <ErrorMessage
                errorType={loanInventoryError}
                header={
                  loanInventoryError === 'no_data_error'
                    ? t('Pi-Portfolio-Management-Empty-Data-Error-Header')
                    : t('PI-Master-Pool-Premiums-Taxes-API-Error-Header')
                }
                content={
                  loanInventoryError === 'no_data_error'
                    ? t('PI-Master-Pool-Premiums-Taxes-API-Error-Content')
                    : t('PI-Master-Pool-Premiums-Taxes-API-Error-Content')
                }
              />
            </Box>
          )}
          {!isLoading && !isError && renderHistory(mergedData)}
        </Box>
      }
    />
  );
};

export default memo(ViewHistoricalRemarksFlyout);
